import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';

/**
 * Utility to safely push to the dataLayer.
 * This is safe to call even when GTM is not loaded or in an SSR environment.
 */
function pushToDataLayer(data: object) {
  // We only want this to run on the client
  if (typeof window === 'undefined') {
    return;
  }
  // Ensure the dataLayer exists
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(data);
}

/**
 * A hook that listens to Remix's location changes and
 * fires a `page_viewed` event to the GTM dataLayer.
 */
export function useGtmPageView() {
  const location = useLocation();
  const lastTrackedPath = useRef('');

  useEffect(() => {
    // The GTM script in root.tsx handles the initial page view.
    // This effect is responsible for tracking all subsequent client-side navigations.
    const currentPath = location.pathname + location.search;

    // Only track if the path has changed since the last page view.
    if (lastTrackedPath.current !== currentPath) {
      // On the very first render, lastTrackedPath.current will be '',
      // and we don't want to fire an event. We let GTM's own script handle that.
      // On subsequent navigations, this will have a value, so we fire the event.
      if (lastTrackedPath.current) {
        console.log('🚀 Firing page_viewed event for client-side navigation.');
        pushToDataLayer({
          event: 'page_viewed',
          page_path: currentPath,
          page_location: window.location.href,
          page_title: document.title,
        });
      }
      // Update the ref to the current path for the next navigation.
      lastTrackedPath.current = currentPath;
    }
  }, [location]);
}

/**
 * A simple component to activate the page view tracking hook.
 * Render this once in your root layout. It renders nothing to the DOM.
 */
export function GtmPageTracking() {
  useGtmPageView();
  return null;
}